import React from 'react';
import { Search, MapPin, TrendingUp, AlertCircle, CheckCircle2 } from 'lucide-react';
export function ProblemSection() {
  return <section className="py-24 px-6 relative overflow-hidden bg-background" data-id="element-226">
      <div className="max-w-4xl mx-auto relative z-10" data-id="element-227">
        <div className="text-center mb-16" data-id="element-228">
          <h2 className="text-3xl md:text-5xl font-serif font-bold mb-6" data-id="element-229">
            ¿Te encuentran a ti o a{' '}
            <span className="text-transparent bg-clip-text bg-gradient-accent" data-id="element-230">
              tu competencia?
            </span>
          </h2>
          <p className="text-xl text-text-secondary font-light" data-id="element-231">
            Instagram no es suficiente. Cuando un cliente busca "Barbería cerca
            de mí", quiere profesionalismo, no solo un perfil social.
          </p>
        </div>

        {/* Visual Representation */}
        <div className="glass-panel rounded-3xl p-8 mb-16 relative overflow-hidden" data-id="element-232">
          <div className="absolute top-0 right-0 w-64 h-64 bg-primary/10 rounded-full blur-[80px]" data-id="element-233"></div>

          <div className="flex flex-col md:flex-row items-center gap-8" data-id="element-234">
            <div className="flex-1 space-y-6" data-id="element-235">
              <div className="flex items-start gap-4" data-id="element-236">
                <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center shrink-0 border border-white/10" data-id="element-237">
                  <Search className="w-5 h-5 text-accent-start" data-id="element-238" />
                </div>
                <div data-id="element-239">
                  <h3 className="text-lg font-medium text-white mb-2" data-id="element-240">
                    Están buscando ahora mismo
                  </h3>
                  <p className="text-text-secondary text-sm leading-relaxed" data-id="element-241">
                    "mejor spa de uñas cerca de mí" — "barbería en mi ciudad" —
                    "servicio de limpieza confiable".
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4" data-id="element-242">
                <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center shrink-0 border border-white/10" data-id="element-243">
                  <AlertCircle className="w-5 h-5 text-red-400" data-id="element-244" />
                </div>
                <div data-id="element-245">
                  <h3 className="text-lg font-medium text-white mb-2" data-id="element-246">
                    Y lo que ven es a tu competencia
                  </h3>
                  <p className="text-text-secondary text-sm leading-relaxed" data-id="element-247">
                    Con su página web. Con sus fotos. Con sus precios. Con un
                    botón que dice: "Reserva tu cita ahora." Mientras tanto, tu
                    negocio... no aparece.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex-1 w-full" data-id="element-248">
              <div className="bg-[#1A1B23] rounded-2xl p-4 border border-white/5 shadow-2xl" data-id="element-249">
                <div className="flex items-center gap-3 mb-4 pb-4 border-b border-white/5" data-id="element-250">
                  <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center" data-id="element-251">
                    <span className="text-xs font-bold" data-id="element-252">G</span>
                  </div>
                  <div className="h-8 bg-white/5 rounded-full flex-1 flex items-center px-4" data-id="element-253">
                    <span className="text-xs text-text-secondary" data-id="element-254">
                      barbería cerca de mí...
                    </span>
                  </div>
                </div>
                <div className="space-y-3" data-id="element-255">
                  <div className="h-16 bg-white/5 rounded-xl border border-white/5 flex items-center px-4 gap-3" data-id="element-256">
                    <MapPin className="w-4 h-4 text-accent-start" data-id="element-257" />
                    <div className="space-y-2 flex-1" data-id="element-258">
                      <div className="h-2 bg-white/20 rounded w-1/2" data-id="element-259"></div>
                      <div className="h-2 bg-white/10 rounded w-1/3" data-id="element-260"></div>
                    </div>
                  </div>
                  <div className="h-16 bg-white/5 rounded-xl border border-white/5 flex items-center px-4 gap-3 opacity-50" data-id="element-261">
                    <MapPin className="w-4 h-4 text-text-secondary" data-id="element-262" />
                    <div className="space-y-2 flex-1" data-id="element-263">
                      <div className="h-2 bg-white/20 rounded w-2/3" data-id="element-264"></div>
                      <div className="h-2 bg-white/10 rounded w-1/4" data-id="element-265"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Copy Content */}
        <div className="space-y-12" data-id="element-266">
          <div className="prose prose-invert max-w-none" data-id="element-267">
            <p className="text-lg text-text-secondary leading-relaxed" data-id="element-268">
              Tienes talento. Tienes experiencia. Tu trabajo es bueno — tus
              clientes actuales lo saben. Pero hay cientos de personas en tu
              área que necesitan lo que tú ofreces y no tienen forma de
              encontrarte.
            </p>
          </div>

          <div className="border-l-2 border-accent-start pl-6 py-2" data-id="element-269">
            <h3 className="text-2xl font-serif font-medium mb-4" data-id="element-270">
              Instagram No Es Suficiente. WhatsApp Tampoco.
            </h3>
            <p className="text-text-secondary leading-relaxed mb-4" data-id="element-271">
              Sé lo que estás pensando: "Pero yo tengo redes sociales..." Y está
              bien. Las redes sociales sirven. Pero tienen un problema enorme:{' '}
              <strong className="text-white" data-id="element-272">No son tuyas.</strong>
            </p>
            <p className="text-text-secondary leading-relaxed" data-id="element-273">
              Mañana el algoritmo cambia y tu publicación no la ve nadie. Tu
              cuenta puede ser bloqueada sin razón. El alcance orgánico cae. Y
              lo más importante: una cuenta de Instagram no genera confianza
              profesional.
            </p>
          </div>

          <div className="glass-panel rounded-2xl p-8" data-id="element-274">
            <h3 className="text-2xl font-serif font-medium mb-6 flex items-center gap-3" data-id="element-275">
              <CheckCircle2 className="w-6 h-6 text-primary" data-id="element-276" />
              La Solución: Tu Escaparate Digital Profesional
            </h3>
            <p className="text-text-secondary leading-relaxed mb-6" data-id="element-277">
              Imagina esto: Un cliente nuevo llega a tu ciudad, busca en Google
              el servicio que tú ofreces, y lo primero que aparece es tu página
              web. Ve tus servicios. Ve tus fotos. Lee sobre ti. Siente
              confianza inmediatamente. Y con un solo clic... te contacta
              directamente.
            </p>
            <p className="text-white font-medium" data-id="element-278">
              Eso es exactamente lo que hace una Landing Page profesional. No es
              magia. Es simplemente tener la herramienta correcta que el mercado
              ya espera de cualquier negocio serio.
            </p>
          </div>
        </div>
      </div>
    </section>;
}